/**
 * GuestCounter.jsx
 * Created by Michael Bray on 9/22/17.
 *
 * TODO - replace all the dropdown bits with DropdownMenu ... should cut the size
 * of this component in half and give better keyboard support
 */

import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import { get, lowerCase, isEmpty } from 'lodash';

import announce from '../../../helpers/announce';
import GuestCounterRow from './GuestCounterRow';

import * as Icons from '../icons/Icons';

const propTypes = {
    disableTextInput: PropTypes.bool,
    title: PropTypes.string,
    minTotalTitle: PropTypes.string,
    maxTotalTitle: PropTypes.string,
    guestTypes: PropTypes.array,
    minTotalGuests: PropTypes.number,
    maxTotalGuests: PropTypes.number,
    onCountChange: PropTypes.func,
    onClose: PropTypes.func,
    value: PropTypes.shape({}),
    validationErrorClass: PropTypes.string,
    disabled: PropTypes.bool,
    maxTotalMessage: PropTypes.string,
    minTotalMessage: PropTypes.string,
    minimumCountByGuestType: PropTypes.shape({}),
    maximumCountByGuestType: PropTypes.shape({}),
    id: PropTypes.string,
    className: PropTypes.string
};

const defaultProps = {
    id: 'guest-counter',
    disableTextInput: false,
    title: "Guests",
    minTotalTitle: "",
    maxTotalTitle: "",
    disabled: false,
    minTotalGuests: 0,
    maxTotalGuests: 999,
    validationErrorClass: '',
    guestTypes: [
        {
            key: "adult",
            type: "Adult",
            description: "ages 13+"
        },
        {
            key: "youth",
            type: "Youth",
            description: "ages 9 - 12"
        },
        {
            key: "access",
            type: "Access",
            description: "no age required"
        },
        {
            key: "senior",
            type: "Senior",
            description: "ages 65+"
        }
    ],
    maximumCountByGuestType: {},
    minimumCountByGuestType: {},
    className: ""
};

export const getWeight = (guestType) => {
    if (typeof guestType.weight === 'number') {
        return guestType.weight;
    }
    return 1;
};

export default class GuestCounter extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            open: false,
            counts: {}
        };

        this.toggleCounter = this.toggleCounter.bind(this);
        this.closeCounter = this.closeCounter.bind(this);
        this.changeCount = this.changeCount.bind(this);
        this.generateGuestCounterRows = this.generateGuestCounterRows.bind(this);
    }

    componentDidMount() {
        window.addEventListener('click', this.toggleCounter);

        this.setState({
            counts: this.props.value || this.populateCounters(this.props.guestTypes)
        });
    }

    componentDidUpdate(prevProps) {
        const { value, guestTypes } = this.props;

        if (value !== prevProps.value
            || guestTypes !== prevProps.guestTypes) {
            this.setState({
                counts: value || this.populateCounters(guestTypes)
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.toggleCounter);
    }

    // We need to use findDOMNode for this functionality to work
    /* eslint-disable react/no-find-dom-node */
    toggleCounter(event) {
        if (this.props.disabled) {
            return;
        }

        const holder = findDOMNode(this.holder);
        const dropdown = findDOMNode(this.dropdown);

        if (!dropdown.contains(event.target) && this.state.open) {
            this.closeCounter();
        }
        else if (!dropdown.contains(event.target) && holder.contains(event.target)) {
            event.preventDefault();

            if (this.state.open) {
                this.closeCounter();
            }
            else {
                this.setState({
                    open: true
                });
            }
        }
    }
    /* eslint-enable react/no-find-dom-node */

    closeCounter() {
        this.setState({
            open: false
        });

        const { onClose } = this.props;

        if (onClose) {
            onClose(this.state.counts, this.sumCounts());
        }
    }

    populateCounters(guestTypes) {
        const counts = {};

        if (guestTypes.length > 0) {
            guestTypes.forEach((guestType) => {
                if (guestType.count) {
                    counts[guestType.key] = guestType.count;
                }
                else {
                    counts[guestType.key] = 0;
                }
            });
        }

        return counts;
    }

    generateGuestCounterRows(maxTotalGuestsReached) {
        const counts = this.props.value || this.state.counts;
        const { disableTextInput } = this.props;

        return this.props.guestTypes.map((type) => {
            let guestType = { ...type };
            // min/max guest selection for guest type
            const max = get(this.props, `maximumCountByGuestType.${guestType.key}`, 0);
            const min = get(this.props, `minimumCountByGuestType.${guestType.key}`, 0);
            // this method renders every time count changes so validation for count input is properly done here, rather than onCountChange
            const disableIncrement = (
                maxTotalGuestsReached ||
                max !== 0 && max <= counts[guestType.key]
            );
            if (max > 0 && min > 0) {
                // if guest type has both a min and a max, include both in the description in format: "max. #, min. #"
                guestType = { ...guestType, description: `${guestType.description} max. ${max}, min. ${min}` };
            }
            else if (min > 0) {
                // if guest type has only a min, include both in the description in format: " min. #"
                guestType = { ...guestType, description: `${guestType.description} min. ${min}` };
            }
            else if (max > 0) {
                // if guest type has only a max, include both in the description in format: "max. #, min. #"
                guestType = { ...guestType, description: `${guestType.description} max. ${max}` };
            }
            return (
                <GuestCounterRow
                    id={this.props.id}
                    disableTextInput={disableTextInput}
                    key={`guest-counter-${guestType.key}`}
                    guestType={guestType}
                    count={counts[guestType.key]}
                    maxTotalGuestsReached={disableIncrement}
                    changeCount={this.changeCount} />
            )
        })
    }

    changeCount(key, count) {
        const { onCountChange, maximumCountByGuestType } = this.props;
        const uncontrolled = !this.props.value;
        // max guests allowed in total
        const maxTotalGuests = this.props.maxTotalGuests || 999;
        // max guests allowed for current guest type
        const maxForGuestType = maximumCountByGuestType[key] ? maximumCountByGuestType[key] : null;
        let counts;

        // Grab the current state
        if (uncontrolled) {
            counts = this.state.counts;
        }
        else {
            counts = { ...this.props.value };
        }

        // validating input here in the case a user using the keyboard instead of the increment button
        const totalSelectedGuests = this.sumCounts() - counts[key];
        const totalRemaining = maxTotalGuests - totalSelectedGuests;

        // Don't let the new count go over either (a) the total or (b) the guest-type specific maximums
        if (!maxForGuestType && count <= totalRemaining) {
            counts[key] = count;
        }
        else if (maxForGuestType && count <= maxForGuestType && count <= totalRemaining) {
            counts[key] = count;
        }
        else if (maxForGuestType && count > maxForGuestType) {
            counts[key] = maxForGuestType;
            announce(`Error: Input changed to ${maxForGuestType} because there's a maximum of ${maxTotalGuests} guests allowed for the selected guest type.`);
        }
        else {
            counts[key] = totalRemaining;
            announce(`Error: Input changed to ${totalRemaining} because there's a maximum of ${maxTotalGuests} guests.`);
        }
        // Don't let counts go negative
        if (counts[key] < 0) {
            counts[key] = 0;
        }

        if (uncontrolled) {
            // Update the state
            this.setState({
                counts
            });
        }

        if (onCountChange) {
            onCountChange(counts);
        }
    }

    sumCounts() {
        const counts = this.props.value || this.state.counts;
        return this.props.guestTypes.reduce((total, guestType) => {
            return total + (counts[guestType.key] * getWeight(guestType));
        }, 0);
    }

    determinePluralLabel(count) {
        // Grab the title from props
        const title = this.props.title;

        // If the count is 1, and the title is pluralized, remove the s
        // This is obviously a very basic implementation
        if (count === 1 && title.charAt(title.length - 1).toLowerCase() === 's') {
            if (title.toLowerCase().endsWith('passes')) {
                // if title ends with "passes" (e.g. Vehicle Passes), remove the trailing "es"
                return title.slice(0, title.length - 2);
            }
            return title.slice(0, title.length - 1);
        }

        // Otherwise, return the plural title
        return title;
    }

    render() {
        const { className } = this.props;
        // Generate Label for Dropdown
        let label = this.props.title;
        const totalCount = this.sumCounts();
        if (totalCount > 0) {
            label = `${totalCount} ${this.determinePluralLabel(totalCount)}`;
        }

        let showErrorClass = '';

        // If maxTotalGuests is passed in, check it against totalCount
        const { maxTotalGuests } = this.props;
        let maxTotalGuestsReached = false;
        if (maxTotalGuests !== 0 && totalCount >= maxTotalGuests) {
            maxTotalGuestsReached = true;
            showErrorClass = 'rec-show-error';
        }

        // Format the max title
        const maxTotalTitle = this.props.maxTotalTitle !== "" ? ` ${this.props.maxTotalTitle}` : ` ${lowerCase(this.props.title)}`;

        // If minTotalGuests is passed in, check it against totalCount
        const { minTotalGuests } = this.props;
        let minTotalGuestsNotReached = false;
        if (minTotalGuests !== 0 && totalCount < minTotalGuests) {
            minTotalGuestsNotReached = true;
            showErrorClass = 'rec-show-error';
        }

        // Format the min title
        const minTotalTitle = this.props.minTotalTitle !== "" ? ` ${lowerCase(this.props.minTotalTitle)}` : ` ${lowerCase(this.props.title)}`;

        // Generate up or down arrow icon
        let showGuestCounterRows = 'hide';
        let selectBorder = '';
        let icon = <Icons.IconArrowDown alt="Pick a field" />;

        if (this.state.open) {
            showGuestCounterRows = '';
            selectBorder = 'open';
            icon = <Icons.IconArrowUp alt="Pick a field" />;
        }

        const { validationErrorClass } = this.props;
        const maxTotalMessage = isEmpty(this.props.maxTotalMessage)
            ? <p>You can select a maximum of <strong>{this.props.maxTotalGuests}</strong>{maxTotalTitle}.</p>
            : <p>{this.props.maxTotalMessage}</p>;

        const minTotalMessage = isEmpty(this.props.minTotalMessage)
            ? <p>You must select a minimum of <strong>{this.props.minTotalGuests}</strong>{minTotalTitle}.</p>
            : <p>{this.props.minTotalMessage}</p>;

        return (
            <div
                id={this.props.id}
                className={`rec-select-holder ${className}`}
                ref={(holder) => {
                    this.holder = holder;
                }}>
                <button
                    className={`rec-select rec-select-guest-counter ${selectBorder} ${validationErrorClass}`}
                    title={this.props.title}
                    aria-label={label}
                    aria-expanded={this.state.open}
                    disabled={this.props.disabled}>
                    <span className="rec-select-label">
                        {label}
                    </span>
                    <span className="rec-select-icon">
                        {icon}
                    </span>
                </button>
                <div
                    className={`rec-select-options ${showGuestCounterRows}`}
                    ref={(dropdown) => {
                        this.dropdown = dropdown;
                    }}>
                    <div className="rec-guest-counter-wrap">
                        {this.generateGuestCounterRows(maxTotalGuestsReached)}
                    </div>

                    {minTotalGuestsNotReached &&
                        <div role="region" aria-live="polite" className={`rec-min-guests ${showErrorClass}`}>
                            <Icons.IconWarning />{minTotalMessage}
                        </div>
                    }

                    {maxTotalGuestsReached &&
                        <div role="region" aria-live="polite" className={`rec-max-guests ${showErrorClass}`}>
                            <Icons.IconWarning />{maxTotalMessage}
                        </div>
                    }

                    <div className="rec-select-option-last">
                        <button
                            className="rec-close-dropdown-button"
                            onClick={this.closeCounter}
                            aria-label="Close"
                            type="button">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

GuestCounter.propTypes = propTypes;
GuestCounter.defaultProps = defaultProps;
